/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {TextNode} from 'lexical';
import {useCallback, useEffect, useMemo, useState} from 'react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { $createMentionNode, MentionNode } from './MentionNode';

const PUNCTUATION =
  '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']';

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
};

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ['@'].join('');

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]';

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')';

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$',
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    '){0,' +
    ALIAS_LENGTH_LIMIT +
    '})' +
    ')$',
);

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 5;

const mentionsCache = new Map();

let mentionsData = []

const lookupService = {
  search: (string, callback) => {
    setTimeout(() => {
      const results = mentionsData.filter((mention) =>
        mention.name.toLowerCase().includes(string.toLowerCase()),
      );
      callback(results);
    }, 500);
  },
};

// this function stores a cache of possible mentions from the API against the current text fragment
function useMentionLookupService(mentionString) {
  const [results, setResults] = useState([]);

  useEffect(() => {
    const cachedResults = mentionsCache.get(mentionString);

    if (mentionString == null) {
      setResults([]);
      return;
    }

    if (cachedResults === null) {
      return;
    } else if (cachedResults !== undefined) {
      setResults(cachedResults);
      return;
    }

    mentionsCache.set(mentionString, null);
    lookupService.search(mentionString, (newResults) => {
      mentionsCache.set(mentionString, newResults);
      setResults(newResults);
    });
  }, [mentionString]);

  return results;
}

// this function checks if the text matches a mention character pattern
function checkForAtSignMentions(
  text,
  minMatchLength,
) {
  let match = AtSignMentionsRegex.exec(text);

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text);
  }
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[3];
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      };
    }
  }
  return null;
}

function getPossibleQueryMatch(text) {
  return checkForAtSignMentions(text, 1);
}

class MentionTypeaheadOption extends MenuOption {
  name;
  picture;
  urn;

  constructor(name, urn, picture) {
    super(name);
    this.name = name;
    this.urn = urn;
    this.picture = picture;
  }
}

// this is each item in the dropdown list of suggestions
function MentionsTypeaheadMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}) {
  let className = 'item';
  if (isSelected) {
    className += ' selected';
  }
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}>
      {option.picture}
      <span className="text">{option.name}</span>
    </li>
  );
}

const onTextNodeUpdate = function (textNode) {
  const text = textNode.getTextContent();
  // const extracted = text.match(/@\[[(\S+)]\]/);
  // const extracted = text.match(/@\[([^\]]+)\]/);
  const extracted = text.match(/@\[([^\]]+)\]\(([^\)]+)\)/)
  if (extracted) {
    const startsAt = extracted.index;
    const textLength = extracted[0].length;
    const templateKey = extracted[1];
    const urn = extracted[2];

    const newNode = $createMentionNode(templateKey, urn);
    let textBefore = text.slice(0, startsAt);
    let textAfter = text.slice(startsAt + textLength);

    if (startsAt > 0 && textBefore.slice(-1).match(/\w/)) { textBefore = `${textBefore} ` }
    if (textAfter.length == 0 || textAfter.slice(0, 1).match(/\w/)) { textAfter = ` ${textAfter}` }

    textNode.insertBefore(new TextNode(textBefore));
    textNode.insertBefore(newNode);
    textNode.setTextContent(textAfter);
  }
}

function MentionsPlugin() {
  const [editor] = useLexicalComposerContext();

  const [queryString, setQueryString] = useState(null);

  const results = useMentionLookupService(queryString);

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0,
  });

  // this is a memoised list of result options
  const options = useMemo(
    () =>
      results
        .map(
          (result) =>
            new MentionTypeaheadOption(result.name, result.urn, <i className="fe fe-user" />),
        )
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
    [results],
  );

  // this runs when a suggested item is clicked and inserts it into the editor
  // as a MentionNode in place of the current text
  const onSelectOption = useCallback(
    (
      selectedOption,
      nodeToReplace,
      closeMenu,
    ) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(selectedOption.name, selectedOption.urn);
        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
        }
        mentionNode.select();
        closeMenu();
      });
    },
    [editor],
  );

  const checkForMentionMatch = useCallback(
    (text) => {
      const slashMatch = checkForSlashTriggerMatch(text, editor);
      if (slashMatch !== null) {
        return null;
      }
      return getPossibleQueryMatch(text);
    },
    [checkForSlashTriggerMatch, editor],
  );

  useEffect(() => {
    if (!editor) return;
    const removeNodeListener = editor.registerNodeTransform(
      TextNode,
      (node) => {
        if (!node) return;
        const textContent = node.getTextContent();

        if (textContent.includes("_") && textContent.length > 1) {
          node.toggleFormat("italic");
          node.setTextContent(textContent.replace("_", ""));
        }
      }
    );
    return () => removeNodeListener();
  }, [editor]);

  useEffect(() => editor.registerNodeTransform(TextNode, onTextNodeUpdate), [editor]);

  // load real mentions data
  useEffect(() => {
    fetch(`/_sf/api/v1/cms/social_posts/mentions.json`, {
      headers: { "Content-Type": "application/json", credentials: "same-origin" },
    })
    .then((response) => {
      if (response.ok) {
        return response.json();
      }
    })
    .then((data) => mentionsData = data);
  }, [])

  return (
    <LexicalTypeaheadMenuPlugin
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        {selectedIndex, selectOptionAndCleanUp, setHighlightedIndex},
      ) =>
        anchorElementRef.current && results.length
          ? ReactDOM.createPortal(
              <div className="typeahead-popover mentions-menu">
                <ul>
                  {options.map((option, i) => (
                    <MentionsTypeaheadMenuItem
                      index={i}
                      isSelected={selectedIndex === i}
                      onClick={() => {
                        setHighlightedIndex(i);
                        selectOptionAndCleanUp(option);
                      }}
                      onMouseEnter={() => {
                        setHighlightedIndex(i);
                      }}
                      key={option.key}
                      option={option}
                    />
                  ))}
                </ul>
              </div>,
              anchorElementRef.current,
            )
          : null
      }
    />
  );
}

export { MentionsPlugin, MentionNode };
